import { isEqual } from 'lodash';
import { memo } from 'react';

import { type PublicBoard } from '@air/api/types';

import { type GalleryViewRender } from '~/components/Gallery/types';
import { useGetPublicBoardMenuOptionsCallback } from '~/components/PublicBoard/useGetPublicBoardMenuOptionsCallback';
import { GalleryBoard } from '~/components/v2/components/shared/gallery/GalleryBoard';
import {
  type UsePublicGalleryBoardCardPropsParams,
  usePublicGalleryBoardCardProps,
} from '~/components/v2/components/shared/gallery/usePublicGalleryBoardCardProps';
import { usePublicSelectionMenuOptions } from '~/hooks/usePublicSelectionMenuOptions';

export type PublicGalleryBoardProps = GalleryViewRender<PublicBoard> &
  Pick<UsePublicGalleryBoardCardPropsParams, 'onNavigateToBoard'>;

export const PublicGalleryBoard = memo(
  ({ box, data: board, onNavigateToBoard }: PublicGalleryBoardProps) => {
    // The card body (thumbnail, title, click targets, custom fields) is the shared public
    // contract — the editor's canvas card renders the same body through this hook; only the menus
    // below are public-specific.
    const boardCardProps = usePublicGalleryBoardCardProps({ board, onNavigateToBoard });
    const { getSelectionMenuOptions } = usePublicSelectionMenuOptions();
    const { getBoardMenuOptionsCallback } = useGetPublicBoardMenuOptionsCallback();
    const getItemMenuOptions = getBoardMenuOptionsCallback({ item: board });

    return (
      <GalleryBoard
        board={board}
        box={box}
        isSelectable={true}
        key={board.id}
        getItemMenuOptions={getItemMenuOptions}
        getSelectionMenuOptions={getSelectionMenuOptions}
        {...boardCardProps}
      />
    );
  },
  // `container` comes from `react-virtualized` and is a different object every time but may have the same values so we need to deep equal check the props
  isEqual,
);

PublicGalleryBoard.displayName = 'PublicGalleryBoard';
